/**
 * AdvancedGoalsModal Component
 * 
 * Modal for setting advanced nutrition goals with timeline.
 * Extracted from App.js - EXACT same UI, no behavior changes.
 */

import React from 'react';
import { AITransparencyNotice } from '../ui/UtilityComponents';

const AdvancedGoalsModal = ({
  // State
  goalFormMode,
  goalFormData,
  useImperial,
  userProfile,
  analyzing,
  
  // Setters & Handlers
  setGoalFormMode,
  setGoalFormData,
  setShowAdvancedGoals,
  saveAdvancedGoals,
  setUseImperial,
  
  // Utility functions
  kgToLbs
}) => {
  return (
    <div className="modal-overlay" onClick={() => setShowAdvancedGoals(false)}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
        <h2 className="heading-3">🎯 Set Nutrition Goals</h2>
        <p className="modal-description">Customize your calorie and macro targets with a timeline</p>
        <div className="premium-badge">Premium Feature</div>
        
        {/* Goal Mode Toggle */}
        <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', marginTop: '16px' }}>
          <button
            onClick={() => setGoalFormMode('ai')}
            style={{
              flex: 1,
              padding: '12px',
              borderRadius: '8px',
              border: `2px solid ${goalFormMode === 'ai' ? 'var(--brand-primary)' : 'var(--border-color)'}`,
              background: goalFormMode === 'ai' ? 'rgba(33, 150, 243, 0.1)' : 'transparent',
              color: goalFormMode === 'ai' ? 'var(--brand-primary)' : 'var(--text-secondary)',
              cursor: 'pointer',
              fontWeight: goalFormMode === 'ai' ? '600' : '400'
            }}
          >
            🤖 AI-Generated
          </button>
          <button
            onClick={() => setGoalFormMode('manual')}
            style={{
              flex: 1,
              padding: '12px',
              borderRadius: '8px',
              border: `2px solid ${goalFormMode === 'manual' ? 'var(--brand-primary)' : 'var(--border-color)'}`,
              background: goalFormMode === 'manual' ? 'rgba(33, 150, 243, 0.1)' : 'transparent',
              color: goalFormMode === 'manual' ? 'var(--brand-primary)' : 'var(--text-secondary)',
              cursor: 'pointer',
              fontWeight: goalFormMode === 'manual' ? '600' : '400'
            }}
          >
            ✍️ Manual Entry
          </button>
        </div>

        {/* Goal Weight Input */}
        <div style={{ marginBottom: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <label style={{ fontSize: '14px', fontWeight: '600' }}>
              Goal Weight ({useImperial ? 'lbs' : 'kg'})
            </label>
            {/* Imperial/Metric Toggle */}
            {setUseImperial && (
              <button
                type="button"
                onClick={() => setUseImperial(!useImperial)}
                style={{
                  padding: '4px 10px',
                  fontSize: '12px',
                  borderRadius: '6px',
                  border: '1px solid var(--border-color)',
                  background: 'rgba(102, 126, 234, 0.1)',
                  color: 'var(--brand-primary)',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                Switch to {useImperial ? 'kg' : 'lbs'}
              </button>
            )}
          </div>
          <input
            type="text" inputMode="decimal"
            placeholder={`Current: ${useImperial && userProfile?.weight_kg ? kgToLbs(userProfile.weight_kg) : userProfile?.weight_kg || ''}`}
            value={goalFormData.goal_weight}
            onChange={(e) => setGoalFormData({...goalFormData, goal_weight: e.target.value})}
            style={{
              width: '100%',
              padding: '12px',
              borderRadius: '8px',
              border: '1px solid var(--border-color)',
              background: 'var(--input-bg)',
              color: 'var(--text-primary)'
            }}
          />
        </div>

        {/* Goal Pace Selector - Relaxed/Standard/Aggressive */}
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '600' }}>
            Goal Pace
          </label>
          <div style={{ display: 'flex', gap: '8px' }}>
            {[
              { value: 'relaxed', label: '🐢 Relaxed', desc: 'Slow & sustainable' },
              { value: 'standard', label: '⚖️ Standard', desc: 'Balanced approach' },
              { value: 'aggressive', label: '🔥 Aggressive', desc: 'Fastest safe option' }
            ].map(pace => (
              <button
                key={pace.value}
                onClick={() => setGoalFormData({...goalFormData, goal_pace: pace.value})}
                style={{
                  flex: 1,
                  padding: '12px 8px',
                  borderRadius: '8px',
                  border: `2px solid ${goalFormData.goal_pace === pace.value ? '#667eea' : 'var(--border-color)'}`,
                  background: goalFormData.goal_pace === pace.value ? 'rgba(102, 126, 234, 0.1)' : 'transparent',
                  cursor: 'pointer',
                  textAlign: 'center'
                }}
              >
                <span style={{ fontSize: '14px', fontWeight: '600', display: 'block', color: goalFormData.goal_pace === pace.value ? '#667eea' : 'var(--text-primary)' }}>
                  {pace.label}
                </span>
                <span style={{ fontSize: '10px', color: 'var(--text-secondary)', display: 'block', marginTop: '4px' }}>
                  {pace.desc}
                </span>
              </button>
            ))}
          </div>
          <p style={{ margin: '8px 0 0 0', fontSize: '11px', color: 'var(--text-secondary)' }}>
            This sets your daily targets. Shorter timelines require larger deficits/surpluses.
          </p>
        </div>

        {/* Timeline Selection */}
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '600' }}>
            Timeline (Weeks)
          </label>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {[
              { value: '8', label: '8 wks' },
              { value: '12', label: '12 wks' },
              { value: '16', label: '16 wks' },
              { value: '24', label: '24 wks' }
            ].map(opt => (
              <button
                key={opt.value}
                onClick={() => setGoalFormData({...goalFormData, timeline_weeks: opt.value})}
                style={{
                  padding: '10px 16px',
                  borderRadius: '8px',
                  border: `2px solid ${goalFormData.timeline_weeks === opt.value ? '#667eea' : 'var(--border-color)'}`,
                  background: goalFormData.timeline_weeks === opt.value ? 'rgba(102, 126, 234, 0.1)' : 'transparent',
                  color: goalFormData.timeline_weeks === opt.value ? '#667eea' : 'var(--text-primary)',
                  cursor: 'pointer',
                  fontWeight: goalFormData.timeline_weeks === opt.value ? '600' : '400'
                }}
              >
                {opt.label}
              </button>
            ))}
            <select
              value={['8', '12', '16', '24'].includes(goalFormData.timeline_weeks) ? '' : goalFormData.timeline_weeks}
              onChange={(e) => setGoalFormData({...goalFormData, timeline_weeks: e.target.value})}
              style={{
                padding: '10px 12px',
                borderRadius: '8px',
                border: '1px solid var(--border-color)',
                background: 'var(--input-bg)',
                color: 'var(--text-primary)',
                cursor: 'pointer'
              }}
            >
              <option value="">Custom...</option>
              <option value="4">4 weeks</option>
              <option value="6">6 weeks</option>
              <option value="10">10 weeks</option>
              <option value="20">20 weeks</option>
              <option value="36">36 weeks</option>
              <option value="52">52 weeks</option>
            </select>
          </div>
        </div>

        {/* Manual Entry Fields */}
        {goalFormMode === 'manual' && (
          <div style={{ 
            padding: '16px', 
            background: 'rgba(255,255,255,0.03)', 
            borderRadius: '8px',
            marginBottom: '16px'
          }}>
            <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: '600' }}>
              Custom Targets (Optional)
            </h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                  Daily Calories
                </label>
                <input
                  type="text" inputMode="decimal"
                  placeholder="Auto-calculate"
                  value={goalFormData.daily_calorie_target}
                  onChange={(e) => setGoalFormData({...goalFormData, daily_calorie_target: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '6px',
                    border: '1px solid var(--border-color)',
                    background: 'var(--input-bg)',
                    color: 'var(--text-primary)'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                  Protein (g)
                </label>
                <input
                  type="text" inputMode="decimal"
                  placeholder="Auto-calculate"
                  value={goalFormData.protein_g}
                  onChange={(e) => setGoalFormData({...goalFormData, protein_g: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '6px',
                    border: '1px solid var(--border-color)',
                    background: 'var(--input-bg)',
                    color: 'var(--text-primary)'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                  Carbs (g)
                </label>
                <input
                  type="text" inputMode="decimal"
                  placeholder="Auto-calculate"
                  value={goalFormData.carbs_g}
                  onChange={(e) => setGoalFormData({...goalFormData, carbs_g: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '6px',
                    border: '1px solid var(--border-color)',
                    background: 'var(--input-bg)',
                    color: 'var(--text-primary)'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                  Fat (g)
                </label>
                <input
                  type="text" inputMode="decimal"
                  placeholder="Auto-calculate"
                  value={goalFormData.fat_g}
                  onChange={(e) => setGoalFormData({...goalFormData, fat_g: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '6px',
                    border: '1px solid var(--border-color)',
                    background: 'var(--input-bg)',
                    color: 'var(--text-primary)'
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {/* AI Mode Info */}
        {goalFormMode === 'ai' && (
          <div style={{ 
            padding: '16px', 
            background: 'rgba(102, 126, 234, 0.1)', 
            borderRadius: '8px',
            marginBottom: '16px',
            border: '1px solid rgba(102, 126, 234, 0.3)'
          }}>
            <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-secondary)', lineHeight: '1.5' }}>
              🤖 AI will calculate optimal daily calories and macros based on your profile, 
              goal weight, and timeline. You'll receive a personalized explanation of how 
              these targets will help you reach your goal.
            </p>
          </div>
        )}

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
          <button
            className="btn-secondary"
            onClick={() => setShowAdvancedGoals(false)}
            style={{ 
              flex: 1, 
              padding: '14px 20px', 
              fontSize: '15px', 
              fontWeight: '600',
              minHeight: '50px',
              borderRadius: '10px'
            }}
          >
            Cancel
          </button>
          <button
            className="btn-primary"
            onClick={saveAdvancedGoals}
            disabled={analyzing}
            style={{ 
              flex: 1, 
              padding: '14px 20px', 
              fontSize: '15px', 
              fontWeight: '600',
              minHeight: '50px',
              borderRadius: '10px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
            }}
          >
            {analyzing ? (goalFormMode === 'ai' ? '🤖 Generating...' : 'Saving...') : 'Save Goals'}
          </button>
        </div>
        
        <AITransparencyNotice />
      </div>
    </div>
  );
};

export default AdvancedGoalsModal;
